import {
  parseChordToken,
  Quality,
  SeventhQuality,
  DEGREE_TO_SEMITONE_MAJOR,
  DEGREE_TO_SEMITONE_HARM_MINOR,
  midiToNote,
} from "./romanParser";
import { noteNameToMidi } from "./chordUtils";

/**
 * Converts a Roman numeral chord to standard chord symbol notation
 * @param roman Roman numeral (e.g., "V7", "ii", "IVmaj7")
 * @param key Key signature (e.g., "C", "F#", "Bb")
 * @param mode "major" or "minor"
 * @returns Chord symbol (e.g., "G7", "Dm", "Fmaj7")
 * @throws Error if the Roman numeral is invalid
 */
export function romanToChordSymbol(
  roman: string,
  key: string,
  mode: "minor" | "major"
): string {
  try {
    // Basic validation: ensure the string contains valid Roman numeral characters
    // This catches cases like "INVALID" which might partially match
    const trimmedRoman = roman.trim();
    const [mainPart] = trimmedRoman.split("/");

    // Check that it starts with optional accidentals, then a valid Roman numeral
    const romanMatch = mainPart.match(/^([#♯b♭]*)([IViv]+)/);
    if (!romanMatch) {
      throw new Error(`Invalid Roman numeral: ${roman}`);
    }

    const afterRoman = mainPart.slice(romanMatch[0].length);

    // Check for obviously invalid patterns like "INVALID" where a single Roman numeral
    // is followed by capital letters that don't form valid chord notation
    // Valid single-letter Roman numerals followed by capital letters should be checked
    const romanNumeral = romanMatch[2];
    if (romanNumeral.length === 1 && afterRoman.length > 0) {
      // If we have a single Roman numeral followed by text, check if it starts with
      // a capital letter that's not part of valid chord notation
      const firstCharAfter = afterRoman.trim()[0];
      // Valid chord notation after a Roman numeral typically starts with:
      // - lowercase letters (maj, add, sus, dim, etc.)
      // - numbers (7, 9, 11, 13, etc.)
      // - symbols (°, +, etc.)
      // - accidentals (#, b, etc.)
      // Capital letters after a single Roman numeral are suspicious (like "N" in "INVALID")
      if (firstCharAfter && /[A-Z]/.test(firstCharAfter)) {
        // Allow if it's part of a valid pattern (like "VII" which would be caught earlier,
        // or if it's followed by valid chord notation)
        // But reject if it's clearly invalid like "INVALID"
        const validCapitalPatterns = /^(VII|VI|IV|III|II)/;
        if (!validCapitalPatterns.test(afterRoman) && afterRoman.length > 2) {
          // This looks like an invalid pattern (e.g., "INVALID")
          throw new Error(`Invalid Roman numeral: ${roman}`);
        }
      }
    }

    // After the Roman numeral, only allow valid chord notation characters
    const validAfterRoman = /^[°+0-9a-zA-Z#♯b♭\s]*$/.test(afterRoman);

    if (!validAfterRoman) {
      throw new Error(`Invalid Roman numeral: ${roman}`);
    }

    // Parse the Roman numeral
    const token = parseChordToken(roman);

    // Get the key root MIDI note
    const keyRootMidi = noteNameToMidi(key, 4);

    // Select the appropriate scale
    const minor = mode === "minor";
    const scale = minor
      ? DEGREE_TO_SEMITONE_HARM_MINOR
      : DEGREE_TO_SEMITONE_MAJOR;

    // In our minor-mode rendering we use harmonic minor, so flat accidentals
    // are only redundant on degrees 3 and 6 (not 7, which is raised).
    let accidental = token.accidental;
    if (
      minor &&
      accidental === -1 &&
      (token.degree === 3 || token.degree === 6)
    ) {
      accidental = 0; // flat is redundant in minor
    }

    // Calculate the root note MIDI number
    const rootDegree = wrapDegree(token.degree);
    const rootSemitone = keyRootMidi + scale[rootDegree - 1] + accidental;
    const rootNoteFull = midiToNote(rootSemitone);
    let rootNote = rootNoteFull.replace(/\d+$/, ""); // Remove octave

    // Special case: if this is the tonic (degree 1) with no accidental, it must match the key spelling
    if (rootDegree === 1 && accidental === 0) {
      // The tonic should always use the key's spelling
      const keyNormalized = key.replace(/♯/g, "#").replace(/♭/g, "b");
      rootNote = keyNormalized;
    } else {
      // Normalize enharmonic spelling based on key context and accidental direction
      // If there's an accidental (after redundant flat removal), prefer the spelling that matches the accidental direction
      if (accidental !== 0) {
        rootNote = formatNoteNameWithAccidental(rootNote, accidental, key);
      } else {
        // For natural notes, normalize common enharmonics based on key context
        rootNote = normalizeNoteName(rootNote, key);
      }
    }

    // Build the chord quality string
    let qualityStr = "";

    // Handle base quality
    if (token.quality === Quality.MAJ) {
      // Major chords: no suffix for triads, but need to handle 7ths separately
      if (!token.hasSeventh) {
        qualityStr = ""; // Just the root note (e.g., "C")
      }
    } else if (token.quality === Quality.MIN) {
      qualityStr = "m"; // Minor (e.g., "Cm")
    } else if (token.quality === Quality.DIM) {
      qualityStr = "dim"; // Diminished (e.g., "Bdim")
    } else if (token.quality === Quality.AUG) {
      qualityStr = "aug"; // Augmented (e.g., "Caug")
    } else if (token.quality === Quality.SUS2) {
      qualityStr = "sus2"; // Suspended 2nd (e.g., "Csus2")
    } else if (token.quality === Quality.SUS4) {
      qualityStr = "sus4"; // Suspended 4th (e.g., "Csus4")
    } else if (token.quality === Quality.POWER5) {
      qualityStr = "5"; // Power chord (e.g., "C5")
    }

    // Handle 7th quality
    if (token.hasSeventh) {
      if (token.seventhQuality === SeventhQuality.MAJOR) {
        // Major 7th: replace "m" with "maj7" for minor-maj7, or add "maj7" for major
        if (token.quality === Quality.MIN) {
          qualityStr = "mmaj7"; // Minor-major 7th (e.g., "Cmmaj7")
        } else {
          qualityStr = qualityStr === "" ? "maj7" : qualityStr + "maj7"; // Major 7th (e.g., "Cmaj7")
        }
      } else if (token.seventhQuality === SeventhQuality.DIMINISHED) {
        // Diminished 7th
        qualityStr = "dim7"; // (e.g., "Bdim7")
      } else {
        // Minor 7th (dominant 7th for major chords, minor 7th for minor chords)
        if (token.quality === Quality.MIN) {
          qualityStr = "m7"; // Minor 7th (e.g., "Cm7")
        } else {
          qualityStr = qualityStr === "" ? "7" : qualityStr + "7"; // Dominant 7th (e.g., "C7")
        }
      }
    }

    // Handle slash chord extensions (e.g., "Imaj7/9" should be "Cmaj9")
    // If bassDegree is 9 and we have a 7th, treat it as an extension
    if (token.hasSeventh && token.bassDegree === 9 && !token.bassAccidental) {
      // This is likely an extension, not a bass note
      // We'll handle it in the extension logic below
    }

    // Handle extensions (9, 11, 13)
    // These are already included in the 7th quality above, but we need to add the extension number
    // Also check if bassDegree represents an extension
    const bassAsExtension =
      token.bassDegree &&
      [9, 11, 13].includes(token.bassDegree) &&
      !token.bassAccidental;
    if (
      token.hasSeventh &&
      (token.extraOffsets.length > 0 || bassAsExtension)
    ) {
      // Check if we have a 9th, 11th, or 13th
      const has9th =
        token.extraOffsets.some((offset) => offset === 14) ||
        (bassAsExtension && token.bassDegree === 9);
      const has11th =
        token.extraOffsets.some((offset) => offset === 17) ||
        (bassAsExtension && token.bassDegree === 11);
      const has13th =
        token.extraOffsets.some((offset) => offset === 21) ||
        (bassAsExtension && token.bassDegree === 13);

      if (has13th) {
        // Replace 7, 9, or 11 with 13 if present
        if (qualityStr.includes("maj7")) {
          qualityStr = qualityStr.replace("maj7", "maj13");
        } else if (qualityStr.includes("maj9")) {
          qualityStr = qualityStr.replace("maj9", "maj13");
        } else if (qualityStr.includes("m7")) {
          qualityStr = qualityStr.replace("m7", "m13");
        } else if (qualityStr.includes("m9")) {
          qualityStr = qualityStr.replace("m9", "m13");
        } else if (qualityStr.includes("7")) {
          qualityStr = qualityStr.replace("7", "13");
        } else if (qualityStr.includes("9")) {
          qualityStr = qualityStr.replace("9", "13");
        } else if (qualityStr.includes("11")) {
          qualityStr = qualityStr.replace("11", "13");
        }
      } else if (has11th) {
        // Replace 7 or 9 with 11 if present
        if (qualityStr.includes("maj7")) {
          qualityStr = qualityStr.replace("maj7", "maj11");
        } else if (qualityStr.includes("maj9")) {
          qualityStr = qualityStr.replace("maj9", "maj11");
        } else if (qualityStr.includes("m7")) {
          qualityStr = qualityStr.replace("m7", "m11");
        } else if (qualityStr.includes("m9")) {
          qualityStr = qualityStr.replace("m9", "m11");
        } else if (qualityStr.includes("7")) {
          qualityStr = qualityStr.replace("7", "11");
        } else if (qualityStr.includes("9")) {
          qualityStr = qualityStr.replace("9", "11");
        }
      } else if (has9th) {
        // Replace 7 with 9 if present
        if (qualityStr.includes("maj7")) {
          qualityStr = qualityStr.replace("maj7", "maj9");
        } else if (qualityStr.includes("m7")) {
          qualityStr = qualityStr.replace("m7", "m9");
        } else if (qualityStr.includes("7")) {
          qualityStr = qualityStr.replace("7", "9");
        }
      }
    }

    // Handle 6th chords vs add6
    // Distinguish: "I6" = 6th chord ("C6"), "Iadd6" = add6 chord ("Cadd6")
    const has6th = token.extraOffsets.some((offset) => offset === 9);
    const isAdd6 =
      roman.toLowerCase().includes("add") && has6th && !token.hasSeventh;
    const is6thChord = has6th && !token.hasSeventh && !isAdd6;

    if (is6thChord) {
      // 6th chord (triad + 6th)
      if (token.quality === Quality.MIN) {
        qualityStr = "m6"; // Minor 6th (e.g., "Cm6")
      } else {
        qualityStr = qualityStr === "" ? "6" : qualityStr + "6"; // Major 6th (e.g., "C6")
      }
    } else if (isAdd6) {
      // add6 chord (add the 6th without 7th)
      qualityStr += "add6"; // (e.g., "Cadd6")
    }

    // Handle added tones (add9)
    const hasAdd9 = token.extraOffsets.some(
      (offset) => offset === 14 && !token.hasSeventh
    );
    if (hasAdd9) {
      qualityStr += "add9"; // (e.g., "Cadd9")
    }

    // Handle altered extensions (7#9, 7b9, etc.)
    // Check for altered 9th in extraOffsets
    const altered9th = token.extraOffsets.find(
      (offset) => offset === 15 || offset === 13
    ); // #9 or b9
    if (altered9th && token.hasSeventh) {
      const alteration = altered9th === 15 ? "#" : "b";
      // Replace 7 or 9 with altered version
      if (qualityStr.includes("9")) {
        qualityStr = qualityStr.replace("9", `${alteration}9`);
      } else if (qualityStr.includes("7")) {
        qualityStr = qualityStr.replace("7", `7${alteration}9`);
      }
    }

    return `${rootNote}${qualityStr}`;
  } catch (error) {
    // Re-throw parsing errors
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(`Invalid Roman numeral: ${roman}`);
  }
}

/**
 * Formats a note name with proper enharmonic spelling based on accidental direction and key context
 * @param noteName Note name from midiToNote (e.g., "C", "F#", "Bb", "A#")
 * @param accidental -1 for flat, +1 for sharp, 0 for natural
 * @param key The key signature for context
 * @returns Formatted note name with proper enharmonic spelling
 */
function formatNoteNameWithAccidental(
  noteName: string,
  accidental: number,
  key: string
): string {
  // The accidental is already applied in the MIDI calculation, so we just need to
  // ensure proper enharmonic spelling based on the accidental direction and key context

  // Normalize key name for matching (handle both # and ♯, b and ♭)
  const normalizedKey = key.replace(/♯/g, "#").replace(/♭/g, "b");

  // Check sharp keys first (more specific, like "F#" should match before "F")
  // Keys that typically use sharps: F#, G, D, A, E, B
  const sharpKeys = ["F#", "G", "D", "A", "E", "B"];
  const isSharpKey = sharpKeys.some((k) => normalizedKey.startsWith(k));

  // Keys that typically use flats: Bb, Eb, Ab, Db, Gb, F (but not F#)
  const flatKeys = ["Bb", "Eb", "Ab", "Db", "Gb"];
  // Only check "F" if it's not "F#"
  const isFlatKey =
    !isSharpKey &&
    (flatKeys.some((k) => normalizedKey.startsWith(k)) ||
      normalizedKey === "F");

  // Enharmonic conversions
  const enharmonics: Record<string, { sharp: string; flat: string }> = {
    "C#": { sharp: "C#", flat: "Db" },
    Db: { sharp: "C#", flat: "Db" },
    "D#": { sharp: "D#", flat: "Eb" },
    Eb: { sharp: "D#", flat: "Eb" },
    "F#": { sharp: "F#", flat: "Gb" },
    Gb: { sharp: "F#", flat: "Gb" },
    "G#": { sharp: "G#", flat: "Ab" },
    Ab: { sharp: "G#", flat: "Ab" },
    "A#": { sharp: "A#", flat: "Bb" },
    Bb: { sharp: "A#", flat: "Bb" },
  };

  const conversion = enharmonics[noteName];
  if (conversion) {
    // Prefer spelling that matches accidental direction, but also consider key context
    if (accidental < 0) {
      // Flat accidental - prefer flat spelling, but use key context as tiebreaker
      return isSharpKey ? conversion.sharp : conversion.flat;
    } else if (accidental > 0) {
      // Sharp accidental - prefer sharp spelling, but use key context as tiebreaker
      return isFlatKey ? conversion.flat : conversion.sharp;
    }
  }

  // If no conversion needed or note is natural, normalize based on key
  return normalizeNoteName(noteName, key);
}

/**
 * Normalizes note name based on key context (prefers flats for flat keys, sharps for sharp keys)
 * @param noteName Note name from midiToNote
 * @param key The key signature
 * @returns Normalized note name
 */
function normalizeNoteName(noteName: string, key: string): string {
  // Normalize key name for matching (handle both # and ♯, b and ♭)
  const normalizedKey = key.replace(/♯/g, "#").replace(/♭/g, "b");

  // Check sharp keys first (more specific, like "F#" should match before "F")
  // Keys that typically use sharps: F#, G, D, A, E, B
  const sharpKeys = ["F#", "G", "D", "A", "E", "B"];
  const isSharpKey = sharpKeys.some((k) => normalizedKey.startsWith(k));

  // Keys that typically use flats: Bb, Eb, Ab, Db, Gb, F (but not F#)
  const flatKeys = ["Bb", "Eb", "Ab", "Db", "Gb"];
  // Only check "F" if it's not "F#"
  const isFlatKey =
    !isSharpKey &&
    (flatKeys.some((k) => normalizedKey.startsWith(k)) ||
      normalizedKey === "F");

  // Enharmonic conversions
  const enharmonics: Record<string, { sharp: string; flat: string }> = {
    "C#": { sharp: "C#", flat: "Db" },
    Db: { sharp: "C#", flat: "Db" },
    "D#": { sharp: "D#", flat: "Eb" },
    Eb: { sharp: "D#", flat: "Eb" },
    "F#": { sharp: "F#", flat: "Gb" },
    Gb: { sharp: "F#", flat: "Gb" },
    "G#": { sharp: "G#", flat: "Ab" },
    Ab: { sharp: "G#", flat: "Ab" },
    "A#": { sharp: "A#", flat: "Bb" },
    Bb: { sharp: "A#", flat: "Bb" },
  };

  const conversion = enharmonics[noteName];
  if (conversion) {
    // Always prefer spelling that matches the key signature
    if (isFlatKey) {
      // For flat keys, always use flat spelling
      return conversion.flat;
    } else if (isSharpKey) {
      // For sharp keys, always use sharp spelling
      return conversion.sharp;
    }
    // If key doesn't clearly indicate preference, check if the noteName itself
    // suggests a preference (e.g., if it has a #, prefer sharp; if it has a b, prefer flat)
    if (noteName.includes("#") || noteName.includes("♯")) {
      return conversion.sharp;
    } else if (noteName.includes("b") || noteName.includes("♭")) {
      return conversion.flat;
    }
    // Default: preserve original spelling
    return noteName;
  }

  // If noteName is not in enharmonics map, check if it should be converted based on key
  // For example, if key is "Bb" and noteName is "A#", we should convert to "Bb"
  // This handles cases where Tone.js returns one spelling but the key prefers another
  if (isFlatKey) {
    // Check if noteName is the sharp version of a flat note we should use
    for (const [, conv] of Object.entries(enharmonics)) {
      if (conv.sharp === noteName && conv.flat !== noteName) {
        return conv.flat;
      }
    }
  } else if (isSharpKey) {
    // Check if noteName is the flat version of a sharp note we should use
    for (const [, conv] of Object.entries(enharmonics)) {
      if (conv.flat === noteName && conv.sharp !== noteName) {
        return conv.sharp;
      }
    }
  }

  return noteName;
}

/**
 * Keep degree within 1-7
 */
function wrapDegree(d: number): number {
  return ((((d - 1) % 7) + 7) % 7) + 1;
}
